import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ActivitiesGateway } from '@wilson/api/gateway';
import {
  Activity,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  Shift,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import {
  DropZoneEventThrottlerDirective,
  GanttDateToPositionPxPipe,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  GanttShiftEndTimePipe,
  GanttShiftStartTimePipe,
  ShiftTimelinePipesModule,
} from '@wilson/shift-timeline/pipes';
import {
  CreateNewViaDragAndDropService,
  DEFAULT_ACTIVITY_SNAPPING_MINUTES,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import {
  SetDemarcatorRange,
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
  TimelineRemoveActivityFromShiftV2,
  TimelineStaysSuggestionService,
  TimelineUpdateShiftV2,
  TimelineUpsertActivityV2,
} from '@wilson/shift-timeline/state';
import {
  addMilliseconds,
  differenceInMilliseconds,
  subMilliseconds,
} from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subscription, firstValueFrom } from 'rxjs';
import { ActivityDropZoneHelperService } from '../../components/activity-drop-zone/activity-drop-zone-helper.service';
import { DragAndDropHelperService } from '../../services/drag-and-drop-helper.service';
import {
  MouseMovementHelperService,
  SnapInformation,
} from '../../services/mouse-movement-helper.service';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';

@Component({
  selector: 'wilson-activity-move-drop-zone-v2',
  imports: [ShiftTimelinePipesModule],
  template: `
    @if (shift && activities) {
      <div class="activityDropZone">
        @if (snappingLeftPx || snappingRightPx) {
          <div
            class="snapping-line"
            [style.left.px]="snappingLeftPx"
            [style.right.px]="snappingRightPx"
          ></div>
        }
      </div>
    }
  `,
  styleUrls: ['./activity-move-drop-zone-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GanttShiftStartTimePipe,
    GanttShiftEndTimePipe,
    GanttStartingPixelPipe,
    GanttDateToPositionPxPipe,
    GanttEndingPixelPipe,
  ],
})
export class ActivityMoveDropZoneV2Component
  extends DropZoneEventThrottlerDirective
  implements OnInit, OnDestroy
{
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly activitiesService = inject(ActivitiesGateway);
  private readonly activityDropZoneHelperService = inject(
    ActivityDropZoneHelperService,
  );
  private readonly ganttActivityStartingPixelPipe = inject(
    GanttStartingPixelPipe,
  );
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly ganttActivityEndingPixelPipe = inject(GanttEndingPixelPipe);
  private readonly dragAndDropHelperService = inject(DragAndDropHelperService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly mouseMovementHelperService = inject(
    MouseMovementHelperService,
  );
  private readonly ganttShiftStartTimePipe = inject(GanttShiftStartTimePipe);
  private readonly ganttShiftEndTimePipe = inject(GanttShiftEndTimePipe);
  private readonly timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );

  @Input() userId!: string | undefined;
  @Input() shift!:
    | (Shift &
        WithPreparedAttributes & {
          id: string;
        })
    | undefined;

  activities!: ActivityWithCategoryAndLocationsAndReportedDetails[];
  activityWidth!: number;
  activity!: ActivityWithCategoryAndLocationsAndReportedDetails | null;
  subscription!: Subscription;
  snappingInformation: SnapInformation[] = [];
  snappingLeftPx: number | null = null;
  snappingRightPx: number | null = null;

  private zoomLevel = this.store.selectSnapshot(
    ShiftTimelineSettingsState.zoomLevel,
  );
  private dropStartDateTime!: Date;

  async ngOnInit(): Promise<void> {
    if (this.shift) {
      this.activities = this.store.selectSnapshot(
        ShiftTimelineDataState.shiftActivitiesArray(this.shift.id),
      );
      if (this.activities) {
        this.activity = await firstValueFrom(
          this.activityDropZoneHelperService.activity$,
        );
        if (this.activity) {
          const activityToMove = { ...this.activity };

          const activityLeftPx = await firstValueFrom(
            this.ganttActivityStartingPixelPipe.transform(
              activityToMove.startDatetime,
            ),
          );
          const activityRightPx = await firstValueFrom(
            this.ganttActivityEndingPixelPipe.transform([
              activityToMove.startDatetime,
              activityToMove.endDatetime,
            ]),
          );
          const frameLength = this.store.selectSnapshot(
            ShiftTimelineState.frameLength,
          );
          this.activityWidth = frameLength - activityRightPx - activityLeftPx;
        }
        await this.setSnapping();
      }
    }
  }

  async setSnapping(): Promise<void> {
    this.subscription = this.dragAndDropModeService.isSnapping$.subscribe(
      async (isSnapping) => {
        if (isSnapping) {
          const snappingStepSize = await firstValueFrom(
            this.dragAndDropModeService.snappingStepSize$,
          );
          this.snappingInformation =
            this.mouseMovementHelperService.generateSnappingInformation(
              this.activities || [],
              this.activity?.id || null,
              snappingStepSize,
            );
        }
      },
    );
  }

  protected async onDragOverHandler(offsetX: number): Promise<void> {
    const shiftToUpdateId = this.shift?.id;
    const frameLength = this.store.selectSnapshot(
      ShiftTimelineState.frameLength,
    );

    if (this.activity && this.userId && shiftToUpdateId) {
      const activityDuration = differenceInMilliseconds(
        new Date(this.activity.endDatetime),
        new Date(this.activity.startDatetime),
      );
      const activityToMove = { ...this.activity };

      this.dragAndDropModeService.setDraggingItemId(activityToMove.id || null);

      let actualLeftPx = offsetX - this.activityWidth / 2;
      let actualRightPx = frameLength - (actualLeftPx + this.activityWidth);

      let startSnapDateTime = null;
      let endSnapDateTime = null;

      const snappingStepSize = await firstValueFrom(
        this.dragAndDropModeService.snappingStepSize$,
      );

      let startDate = this.dragAndDropHelperService.calculateTimeFromPx(
        actualLeftPx + DragAndDropModeService.SHADOW_SHIFT_ALLOWANCE_PIXEL,
        snappingStepSize,
      );
      let endDate = addMilliseconds(startDate, activityDuration);

      if (this.snappingInformation.length) {
        const startSnapInfo = this.mouseMovementHelperService.findSnapInfo(
          actualLeftPx,
          this.snappingInformation,
        );
        const leftOffsetXOfRightEdge = frameLength - actualRightPx;
        const endSnapInfo = this.mouseMovementHelperService.findSnapInfo(
          leftOffsetXOfRightEdge,
          this.snappingInformation,
        );

        if (startSnapInfo !== null) {
          actualLeftPx = startSnapInfo.snapPx;
          startSnapDateTime = startSnapInfo.snapDateTime;
          actualRightPx = frameLength - (actualLeftPx + this.activityWidth);
          this.snappingLeftPx =
            actualLeftPx + DragAndDropModeService.SHADOW_SHIFT_ALLOWANCE_PIXEL;
          this.snappingRightPx = null;
          this.dragAndDropModeService.setCustomSnappingStepSize(1);
          startDate = new Date(startSnapDateTime);
          endDate = addMilliseconds(startDate, activityDuration);
        } else if (endSnapInfo !== null) {
          actualRightPx = frameLength - endSnapInfo.snapPx;
          endSnapDateTime = endSnapInfo.snapDateTime;
          actualLeftPx = frameLength - (actualRightPx + this.activityWidth);
          this.snappingRightPx =
            actualRightPx - DragAndDropModeService.SHADOW_SHIFT_ALLOWANCE_PIXEL;
          this.snappingLeftPx = null;
          this.dragAndDropModeService.setCustomSnappingStepSize(1);
          endDate = new Date(endSnapDateTime);
          startDate = subMilliseconds(endDate, activityDuration);
        } else {
          this.dragAndDropModeService.setCustomSnappingStepSize(
            DEFAULT_ACTIVITY_SNAPPING_MINUTES,
          );
          this.snappingLeftPx = null;
          this.snappingRightPx = null;
        }
      }
      this.dropStartDateTime = startDate;
      const currentDemarcatorRange = {
        left: this.dragAndDropHelperService.calculatePxFromTime({
          time: startDate.toISOString(),
          snappingStepSize,
          skipShadowShiftAllowance: true,
        }),
        right:
          actualRightPx - DragAndDropModeService.SHADOW_SHIFT_ALLOWANCE_PIXEL,
        shiftDuration: activityDuration,
        adjustmentNeeded: false,
      };

      this.store.dispatch(
        new TimelineUpsertActivityV2(
          [
            {
              ...activityToMove,
              startDatetime: startDate.toISOString(),
              endDatetime: endDate.toISOString(),
            },
          ],
          shiftToUpdateId,
        ),
      );
      this.updateShiftPreview();
      this.dragAndDropModeService.setDisplayMovingDemarcatorLine(true);
      this.dragAndDropModeService.setDisplayTimeVisible(true);
      this.dragAndDropModeService.setDraggingItemStartDatetime(startDate);
      this.dragAndDropModeService.setDraggingItemEndDatetime(endDate);
      this.dragAndDropModeService.setIsMovingItemLeftRight(true);
      this.dragAndDropModeService.setHorizontalDemarcatorRange(
        currentDemarcatorRange,
      );
    }
  }

  private getAccurateShiftStartDateTime(
    activities: ActivityWithCategoryAndLocationsAndReportedDetails[],
  ): string {
    activities.sort((a, b) => a.startDatetime.localeCompare(b.startDatetime));
    return activities[0].startDatetime;
  }

  private getAccurateShiftEndDateTime(
    activities: ActivityWithCategoryAndLocationsAndReportedDetails[],
  ): string {
    activities.sort((a, b) => a.endDatetime.localeCompare(b.endDatetime));
    return activities[activities.length - 1].endDatetime;
  }

  private updateShiftPreview(): void {
    if (this.shift) {
      const allActivities = this.store.selectSnapshot(
        ShiftTimelineDataState.shiftActivitiesArray(this.shift.id),
      );
      const accurateStartDateTime =
        this.getAccurateShiftStartDateTime(allActivities);
      const accurateEndDateTime =
        this.getAccurateShiftEndDateTime(allActivities);
      this.store.dispatch(
        new TimelineUpdateShiftV2({
          ...this.shift,
          startDatetime: accurateStartDateTime,
          endDatetime: accurateEndDateTime,
          isHighlighted: false,
        }),
      );
    }
  }

  protected async onDragLeaveHandler(): Promise<void> {
    const shiftToUpdateId = this.shift?.id;
    const activity = await firstValueFrom(
      this.activityDropZoneHelperService.activity$,
    );
    if (activity && this.userId && shiftToUpdateId) {
      this.store.dispatch([
        new TimelineRemoveActivityFromShiftV2(
          activity.id as string,
          shiftToUpdateId,
        ),
        new TimelineUpsertActivityV2([activity], shiftToUpdateId),
      ]);
      this.updateShiftPreview();
      this.dragAndDropModeService.resetMoveModeSettings(this.zoomLevel);
    }
  }

  protected async onDropHandler(): Promise<void> {
    const activity = await firstValueFrom(
      this.activityDropZoneHelperService.activity$,
    );
    const shiftToUpdateId = this.shift?.id;
    if (
      this.shift &&
      this.activities &&
      activity &&
      this.userId &&
      shiftToUpdateId
    ) {
      const accurateShiftStartDateTimeDetails =
        this.ganttShiftStartTimePipe.transform(this.activities);

      const accurateShiftEndDateTimeDetails =
        this.ganttShiftEndTimePipe.transform(this.activities);

      const dropDateTime = this.dropStartDateTime;
      const activityDuration = differenceInMilliseconds(
        new Date(activity.endDatetime),
        new Date(activity.startDatetime),
      );
      const endDateTime = addMilliseconds(dropDateTime, activityDuration);
      const isActivityAtFirstOrLastIndex = this.isActivityAtFirstOrLastIndex(
        this.activities,
        activity.id as string,
      );
      const isActivityWithinShiftBounds = this.isActivityWithinShiftBounds(
        accurateShiftStartDateTimeDetails.date,
        accurateShiftEndDateTimeDetails.date,
        dropDateTime.toISOString(),
        endDateTime.toISOString(),
      );

      if (isActivityAtFirstOrLastIndex || !isActivityWithinShiftBounds) {
        const userConfirmedPublishedShiftEdit =
          await this.timelineCommonOperationsService.checkAndConfirmPublishedShiftDrop(
            this.shift.publicationStatus,
          );
        if (userConfirmedPublishedShiftEdit) {
          await this.handleDrop(
            activity,
            shiftToUpdateId,
            dropDateTime,
            endDateTime,
          );
        } else {
          this.resetActivityPosition(activity, shiftToUpdateId);
        }
        this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers([
          this.userId,
        ]);
      } else {
        await this.handleDrop(
          activity,
          shiftToUpdateId,
          dropDateTime,
          endDateTime,
        );
      }
    }
  }

  private async handleDrop(
    activity: ActivityWithCategoryAndLocationsAndReportedDetails,
    shiftToUpdateId: string,
    dropDateTime: Date,
    endDateTime: Date,
  ): Promise<void> {
    if (activity && this.userId && shiftToUpdateId) {
      try {
        this.store.dispatch(
          new TimelineUpsertActivityV2(
            [
              {
                ...activity,
                startDatetime: dropDateTime.toISOString(),
                endDatetime: endDateTime.toISOString(),
              },
            ],
            shiftToUpdateId,
          ),
        );

        this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
          this.userId,
        ]);
        this.store.dispatch(new SetDemarcatorRange([]));
        this.createNewViaDragAndDropService.removeUpdatingId(shiftToUpdateId);
        this.activityDropZoneHelperService.setActivity(null);
        this.activityDropZoneHelperService.setUserId(null);
        this.dragAndDropModeService.setMoveActivityDropZone(false);
        this.dragAndDropModeService.resetMoveModeSettings(this.zoomLevel);

        await this.activitiesService.updateActivity({
          id: activity.id,
          startDatetime: dropDateTime.toISOString(),
          endDatetime: endDateTime.toISOString(),
        });
        this.timelineCommonOperationsService.updateUsersAssignmentsState([
          this.userId,
        ]);
      } catch {
        this.store.dispatch([
          new TimelineRemoveActivityFromShiftV2(
            activity.id as string,
            shiftToUpdateId,
          ),
          new TimelineUpsertActivityV2([activity], shiftToUpdateId),
        ]);
        this.dragAndDropModeService.setMoveActivityDropZone(false);
        this.dragAndDropModeService.resetMoveModeSettings(this.zoomLevel);
        this.nzMessageService.error(
          this.translate.instant('general.something_went_wrong'),
        );
      }
    }
  }
  private resetActivityPosition(
    activity: ActivityWithCategoryAndLocationsAndReportedDetails,
    shiftToUpdateId: string,
  ): void {
    this.store.dispatch([
      new TimelineUpsertActivityV2([activity], shiftToUpdateId),
    ]);
    this.store.dispatch(new SetDemarcatorRange([]));
    this.dragAndDropModeService.setMoveActivityDropZone(false);
  }

  private isActivityAtFirstOrLastIndex(
    activities: Activity[],
    activityId: string,
  ): boolean {
    const activityIndex = activities.findIndex(
      (activity) => activity.id === activityId,
    );
    return activityIndex === 0 || activityIndex === activities.length - 1;
  }

  private isActivityWithinShiftBounds(
    shiftStartDateTime: string,
    shiftEndDateTime: string,
    activityStartDatetime: string,
    activityEndDateTime: string,
  ): boolean {
    return (
      shiftStartDateTime < activityStartDatetime &&
      shiftEndDateTime > activityEndDateTime
    );
  }

  override ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
